import "./EditSeasonalHoursPopup.scss";
import { useEffect, useState } from "react";
import Popup from "../../../hoc/Popup/Popup";
import { toast } from "react-toastify";
import HandleConfirmationPopup from "../../HandleConfirmation/HandleConfirmationPopup";
import {
  ISeasonalHoursFacility,
  IWeekdayHoursFacility,
} from "../../../types/facility";
import WeekDaySelector from "../../../hoc/DatePicker/Datepicker";
import {
  deleteSeasonalHours,
  updateSeasonalHours,
} from "../../../api/facility/facilityOpeningTimes";

import {
  COURT_LABELS,
  FacilityCourtType,
} from "../../../pages/AdminHandleFacility/mergeHours";

interface IEditSeasonalHoursPopupProps {
  showPopup: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentSeasonalHours?: ISeasonalHoursFacility;
  /** Every court type this row covers — the edit applies to all of them. */
  courtTypes: FacilityCourtType[];
}

function EditSeasonalHoursPopup(props: IEditSeasonalHoursPopupProps) {
  const { showPopup, onClose, onConfirm, currentSeasonalHours, courtTypes } =
    props;
  const primaryCourtType = courtTypes[0] ?? "INDOOR";

  const [showHandleConfirmationPopup, setShowHandleConfirmationPopup] =
    useState(false);
  const [
    showHandleDeleteConfirmationPopup,
    setShowHandleDeleteConfirmationPopup,
  ] = useState(false);
  const [showDoubleConfirmationPopup, setShowDoubleConfirmationPopup] = useState(false)

  const handleConfirm = () => {
    onConfirm();
    handleClose();
  };
  const handleClose = () => {
    setSeasonalHourData(initialSeasonalData);

    setWeekdayHours(
      Object.entries(weekdays).map(([swedish, english]) => ({
        weekday: english,
        opening_hours: "07:00:00",
        closing_hours: "23:00:00",
        court_type: primaryCourtType,
      }))
    );
    onClose();
  };
  const [loading, setLoading] = useState<boolean>(false);
  const [startTime, setStartTime] = useState("07:00:00");
  const [endTime, setEndTime] = useState("23:00:00");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const initialSeasonalData: ISeasonalHoursFacility = {
    alias: "",
    closed: false,
    end_date: "",
    start_date: "",
    court_type: primaryCourtType,
    weekday_hours: [],
  };

  const [seasonalHourData, setSeasonalHourData] =
    useState<ISeasonalHoursFacility>(initialSeasonalData);

  // The start date the record is stored under, captured when the popup opens.
  // The form's own start_date changes as the admin edits, so it cannot be used
  // to tell the backend where the record currently lives.
  const [originalStartDate, setOriginalStartDate] = useState<string>();
  const weekdays: Record<
    "Måndag" | "Tisdag" | "Onsdag" | "Torsdag" | "Fredag" | "Lördag" | "Söndag",
    string
  > = {
    Måndag: "monday",
    Tisdag: "tuesday",
    Onsdag: "wednesday",
    Torsdag: "thursday",
    Fredag: "friday",
    Lördag: "saturday",
    Söndag: "sunday",
  };

  const reverseWeekdays: Record<string, string> = {
    monday: "Måndag",
    tuesday: "Tisdag",
    wednesday: "Onsdag",
    thursday: "Torsdag",
    friday: "Fredag",
    saturday: "Lördag",
    sunday: "Söndag",
  };

  const [weekdayHours, setWeekdayHours] = useState<IWeekdayHoursFacility[]>(
    Object.entries(weekdays).map(([swedish, english]) => ({
      weekday: english,
      opening_hours: "07:00:00",
      closing_hours: "23:00:00",
      court_type: primaryCourtType,
    }))
  );

  const handleWeekdayTimeChange = (
    index: number,
    timeType: "opening_hours" | "closing_hours",
    e: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>
  ) => {
    let timeValue = e.target.value;

    // Handle blur logic
    if (e.type === "blur") {
      let parts = timeValue.split(":");

      if (parts.length === 2) {
        timeValue += ":00";
      } else if (parts.length === 1 && timeValue.includes(":")) {
        timeValue += "00:00";
      } else if (parts.length === 1) {
        timeValue = timeValue.padStart(2, "0") + ":00:00";
      }
    }

    // Update the relevant weekday's time
    const updatedHours = [...weekdayHours];
    updatedHours[index][timeType] = timeValue;
    setWeekdayHours(updatedHours);
  };

  const handleUpdateSeasonalHours = async () => {
    setLoading(true);
    for (const weekday of weekdayHours) {
      weekday.court_type = primaryCourtType;
      if (weekday.opening_hours > weekday.closing_hours) {
        toast.error(
          `Öppningstiden kan inte vara senare än stängningstiden  (${
            reverseWeekdays[weekday.weekday]
          })`
        );
        setLoading(false);
        return; // Exit the function
      }
    }
    if (startDate > endDate) {
      toast.error("Startdatum kan inte vara senare än slutdatum");
      setLoading(false);
      return;
    }
    if (seasonalHourData.alias === "") {
      toast.error("Namn är obligatoriskt");
      setLoading(false);
      return;
    }

    const dataToUpdate = {
      ...seasonalHourData,
      start_date: startDate.toISOString().split("T")[0],
      end_date: endDate.toISOString().split("T")[0],
      weekday_hours: weekdayHours,
      court_type: primaryCourtType,
      // The start date is part of the primary key, so the backend needs the
      // date the record is actually stored under to locate it — the snapshot
      // taken when the popup opened, not the live value.
      previous_start_date: originalStartDate,
    };

    // The row stands for the same period on several courts; each is its own
    // record, so this is one write per type and they can fail independently.
    const succeeded: string[] = [];
    const failed: string[] = [];

    for (const type of courtTypes) {
      try {
        const response = await updateSeasonalHours({
          ...dataToUpdate,
          court_type: type,
          // The nested weekday rows carry their own court_type, so they have
          // to follow the record they belong to.
          weekday_hours: weekdayHours.map((wh) => ({
            ...wh,
            court_type: type,
          })),
        });
        if (response && !("code" in response)) {
          succeeded.push(COURT_LABELS[type]);
        } else {
          failed.push(COURT_LABELS[type]);
        }
      } catch (error: any) {
        failed.push(COURT_LABELS[type]);
      }
    }

    setLoading(false);

    if (succeeded.length > 0) {
      toast.success(`Avvikande inställning uppdaterad: ${succeeded.join(", ")}`);
    }
    if (failed.length > 0) {
      toast.error(`Misslyckades för: ${failed.join(", ")}`);
    }
    if (succeeded.length > 0) {
      handleConfirm();
    }
  };

  const handleDeleteSeasonalHours = async () => {
    setLoading(true);
    const removed: string[] = [];
    const failed: string[] = [];

    for (const type of courtTypes) {
      try {
        const response = await deleteSeasonalHours(
          seasonalHourData.alias,
          type,
          (originalStartDate ?? seasonalHourData.start_date)?.split("T")[0]
        );
        if (response && !("code" in response)) {
          removed.push(COURT_LABELS[type]);
        } else {
          failed.push(COURT_LABELS[type]);
        }
      } catch (error: any) {
        failed.push(COURT_LABELS[type]);
      }
    }

    setLoading(false);

    if (removed.length > 0) {
      toast.success(`Avvikande inställning raderad: ${removed.join(", ")}`);
    }
    if (failed.length > 0) {
      toast.error(`Misslyckades för: ${failed.join(", ")}`);
    }
    if (removed.length > 0) {
      handleConfirm();
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSeasonalHourData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleClosedToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isClosed = e.target.checked;

    setSeasonalHourData((prev) => ({
      ...prev,
      closed: isClosed,
    }));
  };

  useEffect(() => {
    if (showPopup && currentSeasonalHours) {
      // Copy, don't share — see EditSpecialHoursPopup.
      setSeasonalHourData({ ...currentSeasonalHours });
      setOriginalStartDate(currentSeasonalHours.start_date);
      setStartDate(new Date(currentSeasonalHours.start_date));
      setEndDate(new Date(currentSeasonalHours.end_date));
      setWeekdayHours(currentSeasonalHours.weekday_hours);
    }
  }, [showPopup]);
  return (
    <Popup
      showPopup={props.showPopup}
      onClose={handleClose}
      leftTopElement={
        <span className="text-m">
          Redigera avvikande inställning {currentSeasonalHours?.alias}
        </span>
      }
    >
      <div className="general-popup-wrapper">
        <form
          className="general-popup-form"
          onSubmit={(e) => {
            e.preventDefault();
            setShowHandleConfirmationPopup(true);
          }}
        >
          <div className="general-popup-form-group">
            <div className="general-popup-form-field">
              <label htmlFor="alias" className="text-s">
                Namn
              </label>
              <input
                type="text"
                name="alias"
                value={seasonalHourData.alias}
                onChange={handleInputChange}
                className="text-m"
              />
              <label htmlFor="memberType" className="text-s">
                Start datum
              </label>
              <WeekDaySelector
                textM
                includeWeekdays={false}
                value={startDate}
                onChange={(date) => setStartDate(date)}
              />

              <label htmlFor="memberType" className="text-s">
                Slut datum
              </label>
              <WeekDaySelector
                textM
                includeWeekdays={false}
                value={endDate}
                onChange={(date) => setEndDate(date)}
              />

              <div className="general-popup-flex-between">
                <label className="text-s">Gäller banor</label>
                <div className="popup-court-badges text-s">
                  {courtTypes.map((type) => (
                    <span key={type} className={`court-badge ${type}`}>
                      {COURT_LABELS[type]}
                    </span>
                  ))}
                </div>
              </div>
              <label htmlFor="closed" className="text-m">
                Ska det vara stängt under detta interval ?
              </label>
              <div className="general-popup-flex-between">
                <span className="text-m secondary-text">Stängt</span>
                <label className="toggle text-m">
                  <input
                    type="checkbox"
                    name="closed"
                    checked={seasonalHourData.closed}
                    onChange={(e) => handleClosedToggle(e)}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
          </div>
          {!seasonalHourData.closed &&
            weekdayHours.map((weekdayData, index) => (
              <div className="general-popup-form-group">
                <div className="general-popup-form-field">
                  <div key={weekdayData.weekday} className="weekday-group">
                    <label className="text-s">
                      {reverseWeekdays[weekdayData.weekday]}
                    </label>
                    <div className="general-popup-flex-between">
                      <label className="text-m"> Start </label>
                      <input
                        type="time"
                        value={weekdayData.opening_hours}
                        onChange={(e) =>
                          handleWeekdayTimeChange(index, "opening_hours", e)
                        }
                        onBlur={(e) =>
                          handleWeekdayTimeChange(index, "opening_hours", e)
                        }
                        className="text-m"
                      />
                    </div>
                    <div className="general-popup-flex-between">
                      <label className="text-m">Slut </label>
                      <input
                        type="time"
                        value={weekdayData.closing_hours}
                        onChange={(e) =>
                          handleWeekdayTimeChange(index, "closing_hours", e)
                        }
                        onBlur={(e) =>
                          handleWeekdayTimeChange(index, "closing_hours", e)
                        }
                        className="text-m"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

          <div className="general-popup-bottom-buttons-wrapper flex-between">
            <button
              type="button"
              className="deleteButton text-m"
              onClick={() => setShowHandleDeleteConfirmationPopup(true)}
            >
              Radera inställning
            </button>
            <button type="submit" className="text-m">
              Uppdatera avvikande inställning
            </button>
          </div>
        </form>
      </div>

      <HandleConfirmationPopup
        showPopup={showHandleDeleteConfirmationPopup}
        onClose={() => setShowHandleDeleteConfirmationPopup(false)}
        onConfirm={() => {
          handleDeleteSeasonalHours();
          setShowHandleDeleteConfirmationPopup(false);
        }}
        confirmText="Ja"
        questionText="Är du säker på att du vill radera denna avvikande inställning?"
        denyText="Avbryt"
      />
      <HandleConfirmationPopup
        showPopup={showHandleConfirmationPopup}
        onClose={() => setShowHandleConfirmationPopup(false)}
        onConfirm={() => {

          setShowHandleConfirmationPopup(false);
          setShowDoubleConfirmationPopup(true);

        }}
        confirmText="Ja"
        questionText="Är du säker på att du vill redigera denna avvikande inställning?"
        denyText="Avbryt"
      />
      <HandleConfirmationPopup
        showPopup={showDoubleConfirmationPopup}
        onClose={() => setShowDoubleConfirmationPopup(false)}
        onConfirm={() => {
          handleUpdateSeasonalHours();
          setShowDoubleConfirmationPopup(false);
        }}
        confirmText="Ja"
        questionText="Om bokningar ligger utanför uppdaterade öppettider kommer dessa inte avbokas. Vill du genomföra uppdateringen ändå?"
        denyText="Avbryt"
      />
    </Popup>
  );
}

export default EditSeasonalHoursPopup;
